import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import { styled, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TEAM_LEADER_ROLE, TEAM_MEMBER_ROLE } from "_shared/entity-members/_views/team-roster-table";
import React from "react";
import {
  TEAM_PEOPLE_FILTER_MAXIMUM_LENGTH,
  useBookmarkedCompanyResults,
  useBookmarkedIndividualResults,
  useBookmarkedTeamResults,
  useCoworkerResults,
  useLeaderColleagueResults,
  useMarketplaceSearchResults,
  useNetworkResults,
} from "work/work-agreements/teams/people-picker/_hooks/use-team-people-results";
import FormerLeaderChoice from "work/work-agreements/teams/people-picker/_views/former-leader-choice";
import TeamPeopleResultsList, {
  type SelectionUpdate,
  type TeamPickerPersonStatus,
} from "work/work-agreements/teams/people-picker/_views/team-people-results-list";
import TeamPeopleTray from "work/work-agreements/teams/people-picker/_views/team-people-tray";
import type { TeamPickerPerson } from "work/work-agreements/teams/people-picker/team-picker-person";
import TeamPeopleSelection, {
  type TeamPeopleChange,
  type TeamPeoplePickerMode,
} from "work/work-agreements/teams/people-picker/team-people-selection";
import {
  initialTeamPeopleSource,
  MARKETPLACE_SOURCE_HEADING,
  type TeamLeaderFirm,
  type TeamPeopleSource,
  TeamPeopleSourceId,
  teamPeopleSources,
  teamPeopleSourceTitle,
} from "work/work-agreements/teams/people-picker/team-people-sources";

/** Where the picker remembers the source the viewer used last, in this browser only. */
export const LAST_SOURCE_STORAGE_KEY = "team-people-picker:last-source";
export const NOT_A_COWORKER_LEADER_REASON = "A vendor team's leader must be one of your coworkers";
export const FILTER_TOO_LONG_MESSAGE = `Filter must be ${TEAM_PEOPLE_FILTER_MAXIMUM_LENGTH} characters or fewer.`;
const FILTER_DELAY_MS = 300;

export type TeamPeoplePickerProps = {
  mode: TeamPeoplePickerMode;
  /** The source to open on. Without one the picker opens on the one the viewer used last. */
  initialSource?: TeamPeopleSourceId;
  rosterLeaderUserId?: string;
  rosterLeaderName?: string;
  /**
   * Whether the roster's leader is known to lack the credentials a member needs (#5800). A leader
   * replaced here then cannot stay on as a member, and the picker asks for their removal instead.
   */
  rosterLeaderLacksCredentials?: boolean;
  rosterMemberUserIds: readonly string[];
  /** Why nobody can be made leader here, when nobody can — an actor who may not change the team's identity. */
  leaderUnavailableReason?: string;
  /** When set, only people from this legal entity may lead: a vendor leads its own team. */
  leaderLegalEntityId?: string;
  /**
   * The client's legal entity, set when the client staffs the vendor team (#6859). The client's own
   * staff do not work for the firm it hires, so Coworkers is not offered and no source lists anybody
   * from this firm, as the marketplace lists this picker replaced did not.
   */
  clientLegalEntityId?: string;
  /**
   * The vendor leader's firm, set when the client staffs a vendor team that has a leader (#6860). The
   * client has no coworkers there, so the firm's own people are offered as a source of their own.
   */
  leaderFirm?: TeamLeaderFirm;
  canSearchMarketplace: boolean;
  /** Whether the viewer may read marketplace profiles, which a bookmarked company's people are read through. */
  canReadMarketplaceDirectory: boolean;
  onAdd: (change: TeamPeopleChange) => void;
  onCancel: () => void;
};

const Root = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  width: "100%",
}));

const Body = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
}));

const SourceNav = styled("nav")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRight: `1px solid ${theme.palette.divider}`,
  flex: "0 0 208px",
  overflowY: "auto",
}));

const ResultsColumn = styled("section")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
}));

const ResultsHeader = styled("div")(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
}));

const ResultsScroller = styled("div")(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
}));

const Footer = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2, 2),
}));

function readLastSource(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_SOURCE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function rememberSource(source: TeamPeopleSourceId) {
  try {
    globalThis.localStorage?.setItem(LAST_SOURCE_STORAGE_KEY, source);
  } catch {
    // Remembering is a convenience: a browser that refuses storage just opens on the default.
  }
}

export function addButtonLabel(mode: TeamPeoplePickerMode, count: number): string {
  if (mode === "leader") return "Make leader";
  return count === 0 ? "Add to team" : `Add ${count} to team`;
}

/**
 * Whether the selection's leader would replace the roster's: the one case where the picker has to
 * ask what becomes of somebody nobody chose.
 */
export function replacesRosterLeader(chosenLeader: TeamPickerPerson | undefined, rosterLeaderUserId: string | undefined): boolean {
  return Boolean(chosenLeader && rosterLeaderUserId && chosenLeader.userId !== rosterLeaderUserId);
}

/**
 * Whether the leader being replaced stays on as a member. One who could stays unless the viewer chose
 * otherwise; one who could not has no default, so Add waits until their removal is chosen.
 */
export function formerLeaderStaysFor(choice: boolean | undefined, lacksCredentials: boolean): boolean | undefined {
  if (lacksCredentials) return choice === false ? false : undefined;
  return choice ?? true;
}

function SourceResults(
  props: Readonly<{
    source: TeamPeopleSourceId;
    filter: string;
    selection: TeamPeopleSelection;
    statusOf: (person: TeamPickerPerson) => TeamPickerPersonStatus;
    excludedLegalEntityId?: string;
    leaderFirm?: TeamLeaderFirm;
    onSelectionChange: SelectionUpdate;
  }>,
) {
  const shared = {
    selection: props.selection,
    statusOf: props.statusOf,
    groupsAreCheckable: props.selection.mode === "people",
    excludedLegalEntityId: props.excludedLegalEntityId,
    onSelectionChange: props.onSelectionChange,
  };
  switch (props.source) {
    case TeamPeopleSourceId.Coworkers:
      return <CoworkerResults filter={props.filter} {...shared} />;
    case TeamPeopleSourceId.LeaderColleagues:
      // Offered only with a firm, so a picker without one never opens here.
      return props.leaderFirm ? <LeaderColleagueResults firm={props.leaderFirm} filter={props.filter} {...shared} /> : null;
    case TeamPeopleSourceId.Network:
      return <NetworkResults filter={props.filter} {...shared} />;
    case TeamPeopleSourceId.MarketplaceSearch:
      return <SearchResults filter={props.filter} {...shared} />;
    case TeamPeopleSourceId.BookmarkedIndividuals:
      return <BookmarkedIndividualResults filter={props.filter} {...shared} />;
    case TeamPeopleSourceId.BookmarkedTeams:
      return <BookmarkedTeamResults filter={props.filter} {...shared} />;
    case TeamPeopleSourceId.BookmarkedCompanies:
      return <BookmarkedCompanyResults filter={props.filter} {...shared} />;
  }
}

type ResultsProps = { filter: string } & Omit<React.ComponentProps<typeof TeamPeopleResultsList>, "results" | "emptyMessage">;

// One component per source, so each calls exactly one source's hook and only the open source asks
// the API anything.
function CoworkerResults({ filter, ...rest }: Readonly<ResultsProps>) {
  return <TeamPeopleResultsList results={useCoworkerResults(filter)} emptyMessage="No coworkers match." {...rest} />;
}
function LeaderColleagueResults({ firm, filter, ...rest }: Readonly<ResultsProps & { firm: TeamLeaderFirm }>) {
  const firmName = firm.name?.trim() || "the leader's firm";
  // The firm's people are its published Marketplace profiles, so an empty list says so.
  const message = filter.trim() ? `Nobody at ${firmName} matches.` : `Nobody at ${firmName} has a Marketplace profile you can see.`;
  return <TeamPeopleResultsList results={useLeaderColleagueResults(firm, filter)} emptyMessage={message} {...rest} />;
}
function NetworkResults({ filter, ...rest }: Readonly<ResultsProps>) {
  return <TeamPeopleResultsList results={useNetworkResults(filter)} emptyMessage="Nobody in your network matches." {...rest} />;
}
function SearchResults({ filter, ...rest }: Readonly<ResultsProps>) {
  const message = filter.trim() ? "Nobody in the Marketplace matches." : "Search the Marketplace for people and teams.";
  return <TeamPeopleResultsList results={useMarketplaceSearchResults(filter)} emptyMessage={message} {...rest} />;
}
function BookmarkedIndividualResults({ filter, ...rest }: Readonly<ResultsProps>) {
  return <TeamPeopleResultsList results={useBookmarkedIndividualResults(filter)} emptyMessage="No bookmarked individuals." {...rest} />;
}
function BookmarkedTeamResults({ filter, ...rest }: Readonly<ResultsProps>) {
  return <TeamPeopleResultsList results={useBookmarkedTeamResults(filter)} emptyMessage="No bookmarked teams." {...rest} />;
}
function BookmarkedCompanyResults({ filter, ...rest }: Readonly<ResultsProps>) {
  return <TeamPeopleResultsList results={useBookmarkedCompanyResults(filter)} emptyMessage="No bookmarked companies." {...rest} />;
}

function SourceList(
  props: Readonly<{ sources: TeamPeopleSource[]; current: TeamPeopleSourceId; onPick: (id: TeamPeopleSourceId) => void }>,
) {
  const standalone = props.sources.filter((source) => !source.heading);
  const marketplace = props.sources.filter((source) => source.heading === MARKETPLACE_SOURCE_HEADING);
  const item = (source: TeamPeopleSource) => (
    <ListItemButton
      key={source.id}
      selected={source.id === props.current}
      disabled={Boolean(source.unavailableReason)}
      aria-current={source.id === props.current ? "page" : undefined}
      onClick={() => props.onPick(source.id)}
      sx={{ borderRadius: 1, mx: 1 }}
    >
      <ListItemText primary={source.label} secondary={source.unavailableReason} />
    </ListItemButton>
  );
  return (
    <SourceNav aria-label="Sources">
      <List dense>
        {standalone.map(item)}
        <ListSubheader disableSticky sx={{ backgroundColor: "transparent", lineHeight: 2.5, mt: 1 }}>
          {MARKETPLACE_SOURCE_HEADING}
        </ListSubheader>
        {marketplace.map(item)}
      </List>
    </SourceNav>
  );
}

/** At phone width the sources are one select, with the Marketplace's four under their heading. */
function SourceSelect(
  props: Readonly<{ sources: TeamPeopleSource[]; current: TeamPeopleSourceId; onPick: (id: TeamPeopleSourceId) => void }>,
) {
  const standalone = props.sources.filter((source) => !source.heading);
  const marketplace = props.sources.filter((source) => source.heading === MARKETPLACE_SOURCE_HEADING);
  const option = (source: TeamPeopleSource) => (
    <MenuItem key={source.id} value={source.id} disabled={Boolean(source.unavailableReason)}>
      <ListItemText primary={source.label} secondary={source.unavailableReason} />
    </MenuItem>
  );
  return (
    <TextField
      select
      size="small"
      label="Source"
      value={props.current}
      onChange={(event) => props.onPick(event.target.value as TeamPeopleSourceId)}
    >
      {standalone.map(option)}
      <ListSubheader>{MARKETPLACE_SOURCE_HEADING}</ListSubheader>
      {marketplace.map(option)}
    </TextField>
  );
}

/**
 * The team people picker (#6602): one place to add people to a team roster, as members or as its
 * leader, from Coworkers, the Network, a Marketplace search or any kind of bookmark — one at a time,
 * or a whole team or company at once, unchecking whoever should stay off.
 *
 * Sources on the left, results on the right, the selection in a tray at the foot, and one "Add" that
 * hands the roster the whole change. Nobody is left out in silence: someone already on the team, or
 * whom the team would refuse, is shown with the reason.
 */
export default function TeamPeoplePicker(props: Readonly<TeamPeoplePickerProps>) {
  const {
    mode,
    rosterLeaderUserId,
    rosterLeaderName,
    rosterLeaderLacksCredentials,
    rosterMemberUserIds,
    leaderUnavailableReason,
    leaderLegalEntityId,
    clientLegalEntityId,
    leaderFirm,
    onAdd,
    onCancel,
  } = props;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const sources = teamPeopleSources({
    canSearchMarketplace: props.canSearchMarketplace,
    canReadMarketplaceDirectory: props.canReadMarketplaceDirectory,
    offersCoworkers: !clientLegalEntityId,
    leaderFirm,
  });
  const [source, setSource] = React.useState(() => initialTeamPeopleSource(sources, props.initialSource, readLastSource()));
  const [filterText, setFilterText] = React.useState("");
  const [filter, setFilter] = React.useState("");
  const [selection, setSelection] = React.useState(() => new TeamPeopleSelection(mode));
  const [formerLeaderStaysChoice, setFormerLeaderStaysChoice] = React.useState<boolean | undefined>(undefined);
  // The sources' query classes throw past their limit, so a filter that long is shown as an error and
  // never handed to them; the results stay on the last filter that fit.
  const filterIsTooLong = filterText.trim().length > TEAM_PEOPLE_FILTER_MAXIMUM_LENGTH;

  React.useEffect(() => {
    if (filterIsTooLong) return undefined;
    const timer = setTimeout(() => setFilter(filterText), FILTER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [filterText, filterIsTooLong]);

  function pickSource(next: TeamPeopleSourceId) {
    setSource(next);
    setFilterText("");
    setFilter("");
    rememberSource(next);
  }

  function leaderRefusalFor(person: TeamPickerPerson): string | undefined {
    if (leaderUnavailableReason) return leaderUnavailableReason;
    if (leaderLegalEntityId && person.legalEntityId !== leaderLegalEntityId) return NOT_A_COWORKER_LEADER_REASON;
    return undefined;
  }

  function statusOf(person: TeamPickerPerson): TeamPickerPersonStatus {
    if (person.userId === rosterLeaderUserId) return { choosable: false, checked: true, reason: `On the team · ${TEAM_LEADER_ROLE}` };
    if (rosterMemberUserIds.includes(person.userId)) {
      // A member can still be chosen to lead; that moves them, it does not add them twice.
      if (mode === "leader" && !leaderRefusalFor(person)) return { choosable: true };
      return { choosable: false, checked: true, reason: `On the team · ${TEAM_MEMBER_ROLE}` };
    }
    if (mode === "leader") {
      const refusal = leaderRefusalFor(person);
      return refusal ? { choosable: false, checked: false, reason: refusal } : { choosable: true };
    }
    // A vendor team takes no member whose profile lists no credentials (#5800); a leader is not held to it.
    if (person.credentialsRefusal) {
      return { choosable: false, checked: false, reason: person.credentialsRefusal };
    }
    return { choosable: true };
  }

  const chosenLeader = selection.leader;
  const replacesLeader = replacesRosterLeader(chosenLeader, rosterLeaderUserId);
  const formerLeaderStays = formerLeaderStaysFor(formerLeaderStaysChoice, Boolean(rosterLeaderLacksCredentials));
  const awaitingFormerLeaderChoice = replacesLeader && formerLeaderStays === undefined;

  function add() {
    const change = selection.toChange();
    onAdd(replacesLeader ? { ...change, formerLeaderStays } : change);
  }
  const isMarketplaceSearch = source === TeamPeopleSourceId.MarketplaceSearch;

  return (
    <Root>
      <Body>
        {!isPhone && <SourceList sources={sources} current={source} onPick={pickSource} />}
        <ResultsColumn aria-label={teamPeopleSourceTitle(source, leaderFirm?.name)}>
          <ResultsHeader>
            {isPhone ? (
              <SourceSelect sources={sources} current={source} onPick={pickSource} />
            ) : (
              <Typography variant="subtitle1" component="h3">
                {teamPeopleSourceTitle(source, leaderFirm?.name)}
              </Typography>
            )}
            <TextField
              size="small"
              type="search"
              label={isMarketplaceSearch ? "Search people and teams" : "Filter"}
              value={filterText}
              error={filterIsTooLong}
              helperText={filterIsTooLong ? FILTER_TOO_LONG_MESSAGE : undefined}
              slotProps={{ htmlInput: { maxLength: TEAM_PEOPLE_FILTER_MAXIMUM_LENGTH } }}
              onChange={(event) => setFilterText(event.target.value)}
            />
          </ResultsHeader>
          <ResultsScroller>
            <SourceResults
              source={source}
              filter={filter}
              selection={selection}
              statusOf={statusOf}
              excludedLegalEntityId={clientLegalEntityId}
              leaderFirm={leaderFirm}
              onSelectionChange={setSelection}
            />
          </ResultsScroller>
        </ResultsColumn>
      </Body>
      <Footer>
        <TeamPeopleTray
          selection={selection}
          leaderRefusalFor={leaderRefusalFor}
          onSelectionChange={setSelection}
        />
        {chosenLeader && replacesLeader && (
          <FormerLeaderChoice
            chosenLeaderName={chosenLeader.name}
            formerLeaderName={rosterLeaderName}
            formerLeaderLacksCredentials={Boolean(rosterLeaderLacksCredentials)}
            stays={formerLeaderStays}
            onChange={setFormerLeaderStaysChoice}
          />
        )}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" disabled={selection.count === 0 || awaitingFormerLeaderChoice} onClick={add}>
            {addButtonLabel(mode, selection.count)}
          </Button>
        </Box>
      </Footer>
    </Root>
  );
}
